import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import Icon from './AppIcon';

const GRAD = 'linear-gradient(135deg,#f97316,#ea580c)';
const NON_ACTIVE_BID_STATUSES = ['forfeited', 'rejected', 'cancelled'];
const MAX_VEHICLE_MT = 35;

const paise2inr = p => `₹${(Math.abs(p || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const goodsValuePaise = (qty, pricePerTon) => Math.round(Number(qty || 0) * Number(pricePerTon || 0) * 1.05 * 100);
const hoursRemaining = (allocatedAt) => {
  if (!allocatedAt) return 24;
  const deadline = new Date(allocatedAt).getTime() + 24 * 3600 * 1000;
  return Math.max(0, Math.round((deadline - Date.now()) / (3600 * 1000)));
};

// Shared DO Finance modal — "My Vehicles" (pay + submit truck details) and "Place New Bid".
const DoFinanceModal = ({ isOpen, onClose }) => {
  const [tab, setTab] = useState('pay');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Eligibility
  const [application, setApplication] = useState(null); // { approved_vehicle_limit, limit_status }
  const [activeVehicleCount, setActiveVehicleCount] = useState(0);

  // Quotas (for Place New Bid)
  const [quotas, setQuotas] = useState([]);
  const [quotaId, setQuotaId] = useState('');
  const [qtyInput, setQtyInput] = useState('');
  const [feeInput, setFeeInput] = useState('');
  const [bidVehicles, setBidVehicles] = useState([]);
  const [bidError, setBidError] = useState('');
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidResult, setBidResult] = useState(null);

  // My Vehicles (allocated)
  const [allocatedVehicles, setAllocatedVehicles] = useState([]); // { id, bid_id, quantity_mt, financing_fee_paise, status, payment_status, allocated_at, factory_name, factory_short_code, grade, price_per_ton, booking }
  const [selectedForPay, setSelectedForPay] = useState(new Set());
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  // Submit Truck Details
  const [expandedDetailsId, setExpandedDetailsId] = useState(null);
  const [vehicleNumInput, setVehicleNumInput] = useState('');
  const [driverMobileInput, setDriverMobileInput] = useState('');
  const [detailsError, setDetailsError] = useState('');
  const [submittingDetails, setSubmittingDetails] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Load Razorpay checkout script once
    if (!document.getElementById('razorpay-checkout-script')) {
      const s = document.createElement('script');
      s.id = 'razorpay-checkout-script';
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      document.body.appendChild(s);
    }
    loadAll();
  }, [isOpen]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) { onClose(); return; }
      setUser(authUser);

      const [{ data: apps }, { data: quotaRows }, { data: factoryRows }, { data: myBids }] = await Promise.all([
        supabase.from('trader_od_applications')
          .select('id, status, approved_vehicle_limit, limit_status')
          .eq('user_id', authUser.id).eq('status', 'approved').eq('limit_status', 'active')
          .order('created_at', { ascending: false }).limit(1),
        supabase.from('quotas')
          .select('id, factory_name, factory_short_code, grade, quantity, price_per_ton')
          .eq('availability_status', 'available').gt('quantity', 0),
        supabase.from('factories').select('id, name, short_code').eq('status', 'Active'),
        supabase.from('do_finance_bids').select('id, quota_id, user_id, status').eq('user_id', authUser.id),
      ]);

      setApplication(apps?.[0] ?? null);

      const activeFactoryKeys = new Set((factoryRows ?? []).flatMap(f => [f.name, f.short_code]));
      setQuotas((quotaRows ?? []).filter(q => activeFactoryKeys.has(q.factory_name) || activeFactoryKeys.has(q.factory_short_code)));

      const quotaMap = Object.fromEntries((quotaRows ?? []).map(q => [q.id, q]));
      const myBidIds = (myBids ?? []).map(b => b.id);
      const myBidMap = Object.fromEntries((myBids ?? []).map(b => [b.id, b]));

      if (myBidIds.length === 0) {
        setActiveVehicleCount(0);
        setAllocatedVehicles([]);
        return;
      }

      const { data: myVehicles } = await supabase
        .from('do_finance_bid_vehicles')
        .select('id, bid_id, quantity_mt, financing_fee_paise, status, payment_status, allocated_at')
        .in('bid_id', myBidIds);

      const active = (myVehicles ?? []).filter(v => {
        const bidStatus = myBidMap[v.bid_id]?.status;
        return !NON_ACTIVE_BID_STATUSES.includes(bidStatus) && v.status !== 'rejected';
      });
      setActiveVehicleCount(active.filter(v => v.status !== 'settled' && v.status !== 'forfeited').length);

      const allocated = (myVehicles ?? []).filter(v => v.status === 'allocated');

      // Which paid vehicles already have a booking?
      const paidIds = allocated.filter(v => v.payment_status === 'paid').map(v => v.id);
      let bookingMap = {};
      if (paidIds.length > 0) {
        const { data: bookings } = await supabase
          .from('buyer_bookings')
          .select('id, readable_booking_id, status, do_finance_bid_vehicle_id, assigned_vehicle_number')
          .in('do_finance_bid_vehicle_id', paidIds);
        (bookings ?? []).forEach(b => { bookingMap[b.do_finance_bid_vehicle_id] = b; });
      }

      setAllocatedVehicles(allocated.map(v => {
        const quota = quotaMap[myBidMap[v.bid_id]?.quota_id];
        return {
          ...v,
          quota_id: myBidMap[v.bid_id]?.quota_id ?? null,
          factory_name: quota?.factory_name ?? '—',
          factory_short_code: quota?.factory_short_code ?? '',
          grade: quota?.grade ?? '',
          price_per_ton: quota?.price_per_ton ?? 0,
          booking: bookingMap[v.id] ?? null,
        };
      }));
    } catch (err) {
      console.error('DoFinanceModal loadAll error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const quota = quotas.find(q => q.id === quotaId);
  const remainingLimit = (application?.approved_vehicle_limit ?? 0) - activeVehicleCount - bidVehicles.length;

  // ── Place New Bid ──
  const addBidVehicle = () => {
    setBidError('');
    const qty = Number(qtyInput);
    const feeRupees = Number(feeInput);
    if (!quota) { setBidError('Select a factory quota first'); return; }
    if (!qty || qty <= 0 || qty > MAX_VEHICLE_MT) { setBidError(`Quantity must be 1–${MAX_VEHICLE_MT} MT`); return; }
    if (!feeRupees || feeRupees <= 0) { setBidError('Enter your financing fee for this vehicle'); return; }
    if (remainingLimit <= 0) { setBidError(`Vehicle limit reached (${application?.approved_vehicle_limit} approved, ${activeVehicleCount} already active)`); return; }
    setBidVehicles(prev => [...prev, { id: `local-${Date.now()}`, quantity_mt: qty, financing_fee_paise: Math.round(feeRupees * 100) }]);
    setQtyInput(''); setFeeInput('');
  };
  const removeBidVehicle = (id) => setBidVehicles(prev => prev.filter(v => v.id !== id));
  const bidTotalQty = bidVehicles.reduce((s, v) => s + v.quantity_mt, 0);
  const bidTotalFee = bidVehicles.reduce((s, v) => s + v.financing_fee_paise, 0);

  const handleSubmitBid = async () => {
    if (bidVehicles.length === 0) { setBidError('Add at least one vehicle'); return; }
    setBidSubmitting(true); setBidError(''); setBidResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('do-finance-bid-submit', {
        body: {
          quota_id: quotaId,
          vehicles: bidVehicles.map(v => ({ quantity_mt: v.quantity_mt, financing_fee_paise: v.financing_fee_paise })),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setBidResult({ success: true, vehicle_count: bidVehicles.length, total_qty: bidTotalQty, total_fee: bidTotalFee });
      setBidVehicles([]); setQuotaId('');
    } catch (err) {
      setBidResult({ success: false, error: err.message ?? 'Submission failed' });
    } finally {
      setBidSubmitting(false);
    }
  };

  // ── My Vehicles: payment ──
  const unpaidVehicles = allocatedVehicles.filter(v => v.payment_status === 'unpaid');
  const paidUnbookedVehicles = allocatedVehicles.filter(v => v.payment_status === 'paid' && !v.booking);
  const bookedVehicles = allocatedVehicles.filter(v => v.booking);
  const selectedVehicles = unpaidVehicles.filter(v => selectedForPay.has(v.id));
  const selectedTotal = selectedVehicles.reduce((s, v) => s + v.financing_fee_paise, 0);

  const toggleSelectPay = (id) => {
    setSelectedForPay(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const handlePay = async () => {
    if (selectedVehicles.length === 0) { setPayError('Select at least one vehicle to pay for'); return; }
    setPayError(''); setPaying(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-razorpay-order', {
        body: { purpose: 'do_finance_vehicle_fee', vehicle_ids: selectedVehicles.map(v => v.id) },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'SugarHathi',
        description: data.description,
        order_id: data.order_id,
        handler: async (response) => {
          try {
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-razorpay-payment', {
              body: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                purpose: 'do_finance_vehicle_fee',
              },
            });
            if (verifyError || verifyData?.error) throw new Error(verifyData?.error || verifyError?.message || 'Verification failed');
            setSelectedForPay(new Set());
            await loadAll();
          } catch (err) {
            setPayError(err.message ?? 'Payment verification failed');
          } finally {
            setPaying(false);
          }
        },
        prefill: { contact: user?.phone || '', email: user?.email || '' },
        theme: { color: '#ea580c' },
        modal: { ondismiss: () => setPaying(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setPayError(err.message ?? 'Could not start payment');
      setPaying(false);
    }
  };

  // ── Submit Truck Details ──
  const openDetailsForm = (vehicleId) => {
    setExpandedDetailsId(expandedDetailsId === vehicleId ? null : vehicleId);
    setVehicleNumInput(''); setDriverMobileInput(''); setDetailsError('');
  };

  const submitTruckDetails = async (v) => {
    const num = vehicleNumInput.trim().toUpperCase().replace(/\s/g, '');
    const mobile = driverMobileInput.trim();
    if (!/^[A-Z]{2}\d{1,2}[A-Z]{1,2}\d{4}$/.test(num)) { setDetailsError('Enter a valid vehicle number (e.g. MH12AB1234)'); return; }
    if (!/^[6-9]\d{9}$/.test(mobile)) { setDetailsError('Enter a valid 10-digit driver mobile number'); return; }
    setDetailsError(''); setSubmittingDetails(true);
    try {
      const basicAmount = Number(v.quantity_mt) * Number(v.price_per_ton);
      const gstAmount = basicAmount * 0.05;
      const totalAmount = basicAmount + gstAmount;

      const insertPayload = {
        buyer_id: user.id,
        buyer_email: user.email || '',
        quota_id: v.quota_id,
        factory_name: v.factory_name,
        grade: v.grade,
        quantity: Number(v.quantity_mt),
        rate_per_ton: Number(v.price_per_ton),
        destination: '',
        no_of_trucks: 1,
        total_amount: totalAmount,
        booking_type: 'sugar_only',
        booking_date: new Date().toISOString(),
        status: 'pending',
        assigned_vehicle_number: num,
        driver_mobile: mobile,
        do_finance_bid_id: v.bid_id,
        do_finance_bid_vehicle_id: v.id,
        tds_applicable: false,
        tds_amount: 0,
        net_payable: totalAmount,
      };

      const { error } = await supabase.from('buyer_bookings').insert(insertPayload);
      if (error) throw error;

      setExpandedDetailsId(null);
      await loadAll();
    } catch (err) {
      setDetailsError(err.message ?? 'Failed to create booking');
    } finally {
      setSubmittingDetails(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ background: GRAD }}>
          <div className="flex items-center gap-2">
            <Icon name="IndianRupee" size={18} color="#fff" />
            <h2 className="font-bold text-base text-white">Use My DO Finance</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center">
            <Icon name="X" size={18} color="#fff" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex" style={{ borderBottom: '1px solid #f3f4f6' }}>
          <button onClick={() => setTab('pay')} className="flex-1 py-2.5 text-xs font-bold relative" style={{ color: tab === 'pay' ? '#ea580c' : '#9ca3af' }}>
            My Vehicles {unpaidVehicles.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: '#dc2626', fontSize: '10px' }}>{unpaidVehicles.length}</span>}
            {tab === 'pay' && <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: '#ea580c' }} />}
          </button>
          <button onClick={() => setTab('bid')} className="flex-1 py-2.5 text-xs font-bold relative" style={{ color: tab === 'bid' ? '#ea580c' : '#9ca3af' }}>
            Place New Bid
            {tab === 'bid' && <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: '#ea580c' }} />}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm" style={{ color: '#6b7280' }}>Loading...</p>
            </div>
          )}

          {!loading && tab === 'pay' && (
            <>
              {unpaidVehicles.length === 0 && paidUnbookedVehicles.length === 0 && bookedVehicles.length === 0 && (
                <div className="text-center py-10 space-y-2" style={{ color: '#9ca3af' }}>
                  <Icon name="Truck" size={32} className="mx-auto" /><p className="text-sm">No allocated vehicles yet</p>
                </div>
              )}

              {unpaidVehicles.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}>
                    <Icon name="AlertCircle" size={14} className="shrink-0" />
                    Pay within 24h of allocation or it's auto-forfeited (affects your TradeScore)
                  </div>
                  <p className="text-xs font-semibold" style={{ color: '#374151' }}>Awaiting payment ({unpaidVehicles.length})</p>
                  {unpaidVehicles.map(v => {
                    const hrs = hoursRemaining(v.allocated_at);
                    const isSel = selectedForPay.has(v.id);
                    return (
                      <label key={v.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer"
                        style={{ backgroundColor: isSel ? '#fff7ed' : '#f9fafb', border: isSel ? '1px solid #fdba74' : '1px solid #e5e7eb' }}>
                        <input type="checkbox" checked={isSel} onChange={() => toggleSelectPay(v.id)} className="w-4 h-4 rounded shrink-0" style={{ accentColor: '#ea580c' }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold truncate" style={{ color: '#1f2937' }}>{v.factory_name} · {v.quantity_mt} MT</div>
                          <div className="text-xs" style={{ color: hrs <= 4 ? '#dc2626' : '#9ca3af' }}>{hrs}h left to pay</div>
                        </div>
                        <span className="text-xs font-bold shrink-0" style={{ color: '#065f46' }}>{paise2inr(v.financing_fee_paise)}</span>
                      </label>
                    );
                  })}
                  {payError && <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}><Icon name="AlertCircle" size={14} /> {payError}</div>}
                  {selectedVehicles.length > 0 && (
                    <div className="pt-1 space-y-2">
                      <div className="flex items-center justify-between text-sm px-1">
                        <span style={{ color: '#6b7280' }}>{selectedVehicles.length} selected</span>
                        <span className="font-bold" style={{ color: '#065f46' }}>{paise2inr(selectedTotal)}</span>
                      </div>
                      <button onClick={handlePay} disabled={paying} className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2" style={{ background: paying ? '#d1d5db' : GRAD }}>
                        {paying ? 'Processing…' : <><Icon name="CreditCard" size={16} /> Pay {paise2inr(selectedTotal)} via Razorpay</>}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {paidUnbookedVehicles.length > 0 && (
                <div className="space-y-2 pt-2" style={{ borderTop: unpaidVehicles.length > 0 ? '1px solid #f3f4f6' : 'none' }}>
                  <p className="text-xs font-semibold" style={{ color: '#374151' }}>Paid — ready for truck details ({paidUnbookedVehicles.length})</p>
                  {paidUnbookedVehicles.map(v => {
                    const isFormOpen = expandedDetailsId === v.id;
                    return (
                      <div key={v.id} className="rounded-xl overflow-hidden" style={{ border: '1px solid #bbf7d0' }}>
                        <div className="flex items-center gap-3 px-3 py-2.5" style={{ backgroundColor: '#f0fdf4' }}>
                          <Icon name="CheckCircle2" size={16} style={{ color: '#065f46' }} className="shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold truncate" style={{ color: '#1f2937' }}>{v.factory_name} · {v.quantity_mt} MT</div>
                            <div className="text-xs" style={{ color: '#065f46' }}>{paise2inr(v.financing_fee_paise)} paid</div>
                          </div>
                          <button onClick={() => openDetailsForm(v.id)} className="px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 shrink-0 text-white" style={{ background: GRAD }}>
                            Submit Truck Details <Icon name={isFormOpen ? 'ChevronUp' : 'ChevronDown'} size={12} />
                          </button>
                        </div>
                        {isFormOpen && (
                          <div className="p-3 space-y-3" style={{ backgroundColor: '#fff' }}>
                            <div className="grid grid-cols-2 gap-2 text-xs p-2.5 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
                              <div><span style={{ color: '#9ca3af' }}>Factory</span><div className="font-semibold" style={{ color: '#374151' }}>{v.factory_short_code}</div></div>
                              <div><span style={{ color: '#9ca3af' }}>Grade</span><div className="font-semibold" style={{ color: '#374151' }}>{v.grade}</div></div>
                              <div><span style={{ color: '#9ca3af' }}>Quantity</span><div className="font-semibold" style={{ color: '#374151' }}>{v.quantity_mt} MT</div></div>
                              <div><span style={{ color: '#9ca3af' }}>Rate</span><div className="font-semibold" style={{ color: '#374151' }}>₹{Number(v.price_per_ton).toLocaleString('en-IN')}/ton</div></div>
                              <div className="col-span-2 pt-1" style={{ borderTop: '1px solid #e5e7eb' }}>
                                <span style={{ color: '#9ca3af' }}>Goods value</span>
                                <div className="font-bold" style={{ color: '#065f46' }}>{paise2inr(goodsValuePaise(v.quantity_mt, v.price_per_ton))}</div>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs mb-1" style={{ color: '#6b7280' }}>Vehicle Number</label>
                              <input type="text" value={vehicleNumInput} onChange={e => setVehicleNumInput(e.target.value.toUpperCase())} placeholder="MH12AB1234" className="w-full px-3 py-2 rounded-lg border text-sm font-mono" style={{ borderColor: '#d1d5db' }} />
                            </div>
                            <div>
                              <label className="block text-xs mb-1" style={{ color: '#6b7280' }}>Driver Mobile</label>
                              <input type="tel" value={driverMobileInput} onChange={e => setDriverMobileInput(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="98XXXXXXXX" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#d1d5db' }} />
                            </div>
                            {detailsError && <div className="flex items-center gap-2 text-xs" style={{ color: '#dc2626' }}><Icon name="AlertCircle" size={13} /> {detailsError}</div>}
                            <button onClick={() => submitTruckDetails(v)} disabled={submittingDetails} className="w-full py-2.5 rounded-lg text-xs font-bold text-white" style={{ background: GRAD }}>
                              {submittingDetails ? 'Creating booking...' : 'Confirm & Create Booking'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {bookedVehicles.length > 0 && (
                <div className="space-y-2 pt-2" style={{ borderTop: '1px solid #f3f4f6' }}>
                  <p className="text-xs font-semibold" style={{ color: '#374151' }}>Booked — pending QM approval ({bookedVehicles.length})</p>
                  {bookedVehicles.map(v => (
                    <div key={v.id} className="px-3 py-2.5 rounded-xl" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold font-mono" style={{ color: '#1e40af' }}>{v.booking.readable_booking_id}</span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>Pending QM Approval</span>
                      </div>
                      <div className="text-xs mt-1" style={{ color: '#374151' }}>{v.factory_name} · {v.quantity_mt} MT</div>
                      <div className="text-xs font-mono mt-0.5" style={{ color: '#6b7280' }}>{v.booking.assigned_vehicle_number}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {!loading && tab === 'bid' && (
            !application ? (
              <div className="text-center py-10 space-y-2" style={{ color: '#9ca3af' }}>
                <Icon name="Lock" size={32} className="mx-auto" />
                <p className="text-sm">DO Finance not yet approved for your account</p>
              </div>
            ) : bidResult?.success ? (
              <div className="py-8 text-center space-y-3">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
                  <Icon name="IndianRupee" size={24} color="#065f46" />
                </div>
                <h3 className="font-bold text-lg" style={{ color: '#1f2937' }}>Bid Submitted</h3>
                <p className="text-sm" style={{ color: '#6b7280' }}>{bidResult.vehicle_count} vehicle{bidResult.vehicle_count !== 1 ? 's' : ''} · {bidResult.total_qty} MT · {paise2inr(bidResult.total_fee)} total fee</p>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Admin will review and allocate. Once allocated, pay within 24h in "My Vehicles" to proceed.</p>
                <button onClick={() => { setBidResult(null); setTab('pay'); loadAll(); }} className="px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: GRAD }}>Done</button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium" style={{ backgroundColor: '#fffbeb', color: '#92400e' }}>
                  <Icon name="Truck" size={14} className="shrink-0" />
                  {activeVehicleCount + bidVehicles.length}/{application?.approved_vehicle_limit ?? 0} vehicles used · {remainingLimit} remaining
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>Factory / Quota</label>
                  <select value={quotaId} onChange={e => { setQuotaId(e.target.value); setBidVehicles([]); setBidError(''); }} className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: '#d1d5db' }}>
                    <option value="">Select a quota...</option>
                    {quotas.map(q => <option key={q.id} value={q.id}>{q.factory_name} ({q.factory_short_code}) · {q.grade} · ₹{Number(q.price_per_ton).toLocaleString('en-IN')}/ton</option>)}
                  </select>
                </div>
                {quota && (
                  <>
                    <div className="p-3 rounded-xl space-y-3" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                      <p className="text-xs font-semibold" style={{ color: '#374151' }}>Add a vehicle</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs mb-1" style={{ color: '#6b7280' }}>Quantity (MT, max {MAX_VEHICLE_MT})</label>
                          <input type="number" min="1" max={MAX_VEHICLE_MT} value={qtyInput} onChange={e => setQtyInput(e.target.value)} placeholder="e.g. 32" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#d1d5db' }} />
                        </div>
                        <div>
                          <label className="block text-xs mb-1" style={{ color: '#6b7280' }}>Your financing fee (₹)</label>
                          <input type="number" min="1" value={feeInput} onChange={e => setFeeInput(e.target.value)} placeholder="e.g. 1500" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#d1d5db' }} />
                        </div>
                      </div>
                      {qtyInput && Number(qtyInput) > 0 && (
                        <div className="flex items-center justify-between px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}>
                          <span style={{ color: '#6b7280' }}>Goods value (incl. 5%)</span>
                          <span className="font-bold" style={{ color: '#065f46' }}>{paise2inr(goodsValuePaise(qtyInput, quota.price_per_ton))}</span>
                        </div>
                      )}
                      <button onClick={addBidVehicle} className="w-full py-2 rounded-lg text-xs font-bold text-white flex items-center justify-center gap-1.5" style={{ background: GRAD }}>
                        <Icon name="Plus" size={14} /> Add Vehicle
                      </button>
                    </div>
                    {bidError && <div className="flex items-start gap-2 px-3 py-2 rounded-xl text-xs" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}><Icon name="AlertCircle" size={14} className="shrink-0 mt-0.5" /> {bidError}</div>}
                    {bidResult?.success === false && <div className="flex items-start gap-2 px-3 py-2 rounded-xl text-xs" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}><Icon name="AlertCircle" size={14} className="shrink-0 mt-0.5" /> {bidResult.error}</div>}
                    {bidVehicles.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold" style={{ color: '#374151' }}>Vehicles in this bid ({bidVehicles.length})</p>
                        {bidVehicles.map((v, idx) => (
                          <div key={v.id} className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <div><span className="text-xs font-medium" style={{ color: '#374151' }}>Vehicle {idx + 1} · {v.quantity_mt} MT</span><div className="text-xs font-bold" style={{ color: '#065f46' }}>{paise2inr(v.financing_fee_paise)} fee</div></div>
                            <button onClick={() => removeBidVehicle(v.id)}><Icon name="Trash2" size={14} style={{ color: '#dc2626' }} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {bidVehicles.length > 0 && (
                      <div className="pt-2 space-y-2" style={{ borderTop: '1px solid #f3f4f6' }}>
                        <div className="flex items-center justify-between text-sm">
                          <span style={{ color: '#6b7280' }}>Total: {bidTotalQty} MT, {bidVehicles.length} vehicle{bidVehicles.length !== 1 ? 's' : ''}</span>
                          <span className="font-bold" style={{ color: '#065f46' }}>{paise2inr(bidTotalFee)} fee</span>
                        </div>
                        <button onClick={handleSubmitBid} disabled={bidSubmitting} className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2" style={{ background: GRAD }}>
                          {bidSubmitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Save & Submit'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
          <p className="text-xs text-center" style={{ color: '#9ca3af' }}>
            Rate is fixed to your quota · Vehicle does not leave without full payment
          </p>
        </div>
      </div>
    </div>
  );
};

export default DoFinanceModal;
